import {ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {finalize} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {DataService} from '../services/data.service';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {TableSortState} from '../table-sort';
import {HttpErrorResponse} from '@angular/common/http';
import {ApiResponse, PosTerminal, PosTerminalUpdateRequest} from '../models/api.models';
import {PermissionService} from '../services/permission.service';
import {AdminPermissions} from '../models/admin-permissions';
import {environment} from '../../environments/environment';

interface ErrorPayload extends Partial<ApiResponse> {
    Message?: string;
}

@Component({
    selector: 'app-pos',
    templateUrl: './pos.component.html',
    styleUrls: ['./pos.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PosComponent implements OnInit, OnDestroy {
    terminals: PosTerminal[] = [];
    filteredTerminals: PosTerminal[] = [];
    selectedPos?: PosTerminal;
    originalIdTerminal = '';
    searchText = '';
    currentPage = 1;
    pageSize = 25;
    tableSort = new TableSortState();
    isLoading = false;
    isSaving = false;
    isResettingPairingCode = false;
    isLoadingPosAppVersion = false;
    isSavingPosAppVersion = false;
    canUpdatePos = false;
    canResetPosPairing = false;
    posAppVersion = '';
    originalPosAppVersion = '';
    posAppVersionExists = false;
    posAppVersionLoaded = false;
    posAppVersionError = '';
    loadError = '';
    modalMessage = '';
    modalMessageType = '';
    posMapLatitude = 44.4268;
    posMapLongitude = 26.1025;
    posMapCenter: google.maps.LatLngLiteral = {lat: this.posMapLatitude, lng: this.posMapLongitude};
    posMapZoom = 16;
    posMapOptions: google.maps.MapOptions = {
        fullscreenControl: true,
        gestureHandling: 'greedy',
        mapId: environment.googleMapsMapId,
        mapTypeControl: false,
        scrollwheel: true,
        streetViewControl: false
    };
    private readonly defaultPosAppVersion = '2.0.12';
    private posAppVersionRequestId = 0;
    private sessionSubscription: Subscription;

    constructor(
        private dataService: DataService,
        private restService: RestService,
        private uiBlockerService: UIBlockerService,
        private permissionService: PermissionService
    ) {
    }

    ngOnInit() {
        this.updatePermissions();
        this.sessionSubscription = this.dataService.dataChangedSession.subscribe(() => {
            this.updatePermissions();
            this.loadTerminals();
        });
        this.loadTerminals();
    }

    ngOnDestroy() {
        if (!isNullOrUndefined(this.sessionSubscription)) {
            this.sessionSubscription.unsubscribe();
        }
        document.body.style.overflow = '';
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent) {
        if (isNullOrUndefined(this.selectedPos)
            || this.isSaving
            || this.isResettingPairingCode
            || this.isSavingPosAppVersion) {
            return;
        }
        event.preventDefault();
        this.closeModal();
    }

    loadTerminals() {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier()) || this.isLoading) {
            return;
        }
        this.isLoading = true;
        this.loadError = '';
        this.restService.getPosTerminals()
            .pipe(finalize(() => {
                this.isLoading = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe({
                next: (response) => {
                    const parsed = this.parseResponse(response);
                    if (!this.isErrorResponse(parsed) && Array.isArray(parsed.PosTerminals)) {
                        this.terminals = parsed.PosTerminals;
                        this.filterTerminals();
                    } else {
                        this.terminals = [];
                        this.filteredTerminals = [];
                        this.loadError = this.getResponseMessage(parsed, 'The POS terminals could not be loaded.');
                    }
                },
                error: () => {
                    this.terminals = [];
                    this.filteredTerminals = [];
                    this.loadError = 'The POS terminals could not be loaded. Please try again.';
                }
            });
    }

    onSearchTextChanged(value: string) {
        this.searchText = isNullOrUndefined(value) ? '' : value;
        this.currentPage = 1;
        this.filterTerminals();
    }

    filterTerminals() {
        const search = this.searchText.trim().toLowerCase();
        if (search === '') {
            this.filteredTerminals = this.terminals.slice();
            this.clampCurrentPage();
            return;
        }

        this.filteredTerminals = this.terminals.filter(terminal =>
            this.contains(terminal.IdTerminal, search)
            || this.contains(terminal.ClientName, search)
            || this.contains(terminal.Address, search)
            || this.contains(terminal.Phone, search)
        );
        this.clampCurrentPage();
    }

    getPagedTerminals() {
        const start = (this.currentPage - 1) * this.pageSize;
        const sortedTerminals = this.tableSort.sort(
            this.filteredTerminals,
            (terminal, column) => this.getTerminalSortValue(terminal, column)
        );
        return sortedTerminals.slice(start, start + this.pageSize);
    }

    changePageSize() {
        this.currentPage = 1;
    }

    getTotalPages() {
        return Math.max(1, Math.ceil(this.filteredTerminals.length / this.pageSize));
    }

    getFirstItemNumber() {
        return this.filteredTerminals.length === 0 ? 0 : ((this.currentPage - 1) * this.pageSize) + 1;
    }

    getLastItemNumber() {
        return Math.min(this.currentPage * this.pageSize, this.filteredTerminals.length);
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.getTotalPages()) {
            this.currentPage = page;
        }
    }

    openModal(terminal: PosTerminal) {
        this.selectedPos = JSON.parse(JSON.stringify(terminal)) as PosTerminal;
        this.selectedPos.Language = this.normalizeLanguage(this.selectedPos.Language);
        this.initializeMapCoordinates();
        this.originalIdTerminal = terminal.IdTerminal;
        this.loadPosAppVersion(this.originalIdTerminal);
        this.clearModalMessage();
        document.body.style.overflow = 'hidden';
    }

    closeModal() {
        if (this.isSaving || this.isResettingPairingCode || this.isSavingPosAppVersion) {
            return;
        }
        this.posAppVersionRequestId++;
        this.selectedPos = undefined;
        this.originalIdTerminal = '';
        this.resetPosAppVersionState();
        this.clearModalMessage();
        document.body.style.overflow = '';
    }

    onModalBackdropClick(event: MouseEvent) {
        if (event.target === event.currentTarget) {
            this.closeModal();
        }
    }

    onPosMarkerDragEnd(event: google.maps.MapMouseEvent) {
        if (!this.canUpdatePos
            || isNullOrUndefined(this.selectedPos)
            || isNullOrUndefined(event)
            || isNullOrUndefined(event.latLng)) {
            return;
        }

        const latitude = Number(event.latLng.lat());
        const longitude = Number(event.latLng.lng());
        if (!this.isValidLatitude(latitude) || !this.isValidLongitude(longitude)) {
            return;
        }

        this.setPosMapCenter(latitude, longitude);
        this.selectedPos.Latitude = Number(latitude.toFixed(7));
        this.selectedPos.Longitude = Number(longitude.toFixed(7));
        this.clearModalMessage();
    }

    saveTerminal() {
        if (!this.canUpdatePos
            || isNullOrUndefined(this.selectedPos)
            || this.isSaving
            || this.isLoadingPosAppVersion
            || this.isSavingPosAppVersion) {
            return;
        }

        const validationMessage = this.validateTerminal();
        if (validationMessage !== '') {
            this.setModalMessage(validationMessage, 'error');
            return;
        }

        const payload: PosTerminalUpdateRequest = Object.assign({}, this.selectedPos, {
            OriginalIdTerminal: this.originalIdTerminal,
            Latitude: Number(this.selectedPos.Latitude),
            Longitude: Number(this.selectedPos.Longitude),
            MaxCarsCount: Number(this.selectedPos.MaxCarsCount)
        });

        this.isSaving = true;
        this.clearModalMessage();
        this.restService.updatePosTerminal(payload)
            .pipe(finalize(() => {
                this.isSaving = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe({
                next: (response) => {
                    const parsed = this.parseResponse(response);
                    if (this.isErrorResponse(parsed)) {
                        this.setModalMessage(this.getResponseMessage(parsed, 'The POS could not be saved.'), 'error');
                        return;
                    }
                    const terminal = parsed.PosTerminal;
                    if (isNullOrUndefined(terminal)) {
                        this.setModalMessage('The POS could not be saved.', 'error');
                        return;
                    }
                    this.replaceTerminal(this.originalIdTerminal, terminal);
                    this.selectedPos = JSON.parse(JSON.stringify(terminal)) as PosTerminal;
                    this.initializeMapCoordinates();
                    this.originalIdTerminal = terminal.IdTerminal;
                    this.setModalMessage('POS settings saved successfully.', 'success');
                },
                error: (error: HttpErrorResponse) => this.setModalMessage(
                    this.getResponseMessage(error, 'The POS could not be saved.'), 'error')
            });
    }

    resetPairingCode() {
        if (!this.canResetPosPairing
            || isNullOrUndefined(this.selectedPos)
            || !this.isPaired(this.selectedPos)
            || this.isResettingPairingCode
            || this.isSavingPosAppVersion) {
            return;
        }

        this.isResettingPairingCode = true;
        this.clearModalMessage();
        this.restService.resetPosPairingCode(this.originalIdTerminal)
            .pipe(finalize(() => {
                this.isResettingPairingCode = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe({
                next: (response) => {
                    const parsed = this.parseResponse(response);
                    if (this.isErrorResponse(parsed)) {
                        this.setModalMessage(
                            this.getResponseMessage(parsed, 'The pairing code could not be reset.'), 'error');
                        return;
                    }
                    const terminal = parsed.PosTerminal;
                    if (isNullOrUndefined(terminal)) {
                        this.setModalMessage('The pairing code could not be reset.', 'error');
                        return;
                    }
                    this.selectedPos.PairingCode = terminal.PairingCode;
                    const sourceTerminal = this.terminals.find(item => item.IdTerminal === this.originalIdTerminal);
                    if (!isNullOrUndefined(sourceTerminal)) {
                        sourceTerminal.PairingCode = terminal.PairingCode;
                    }
                    this.filterTerminals();
                    this.setModalMessage('A new pairing code was generated.', 'success');
                },
                error: (error: HttpErrorResponse) => this.setModalMessage(
                    this.getResponseMessage(error, 'The pairing code could not be reset.'), 'error')
            });
    }

    isPaired(terminal: PosTerminal | undefined) {
        return !isNullOrUndefined(terminal) && terminal.IsPaired;
    }

    getPairingCode(terminal: PosTerminal | undefined) {
        return this.isPaired(terminal) ? '' : String(terminal.PairingCode);
    }

    loadPosAppVersion(deviceId: string) {
        if (String(deviceId || '').trim() === '') {
            this.resetPosAppVersionState();
            this.posAppVersionError = 'A terminal ID is required to load the POS version.';
            return;
        }

        const normalizedDeviceId = String(deviceId).trim();
        const requestId = ++this.posAppVersionRequestId;
        this.resetPosAppVersionState();
        this.isLoadingPosAppVersion = true;
        this.restService.getPosAppVersion(normalizedDeviceId)
            .pipe(finalize(() => {
                if (requestId === this.posAppVersionRequestId) {
                    this.isLoadingPosAppVersion = false;
                    this.uiBlockerService.unBlockUI();
                }
            }))
            .subscribe({
                next: (response) => {
                    if (requestId !== this.posAppVersionRequestId
                        || this.originalIdTerminal !== normalizedDeviceId) {
                        return;
                    }
                    const parsed = this.parseResponse(response);
                    if (this.isErrorResponse(parsed)) {
                        this.posAppVersionError = this.getResponseMessage(
                            parsed, 'The POS app version could not be loaded.');
                        return;
                    }

                    const version = parsed.PosAppVersion;
                    this.posAppVersionExists = !isNullOrUndefined(version);
                    this.posAppVersion = this.posAppVersionExists
                        ? String(version.PosAppVer || '').trim()
                        : this.defaultPosAppVersion;
                    this.originalPosAppVersion = this.posAppVersionExists ? this.posAppVersion : '';
                    this.posAppVersionLoaded = true;
                },
                error: (error: HttpErrorResponse) => {
                    if (requestId === this.posAppVersionRequestId) {
                        this.posAppVersionError = this.getResponseMessage(
                            error, 'The POS app version could not be loaded.');
                    }
                }
            });
    }

    onPosAppVersionChanged(value: string) {
        this.posAppVersion = isNullOrUndefined(value) ? '' : value;
        this.clearModalMessage();
    }

    canSavePosAppVersion() {
        const version = this.posAppVersion.trim();
        return this.canUpdatePos
            && this.posAppVersionLoaded
            && !this.isLoadingPosAppVersion
            && !this.isSavingPosAppVersion
            && !this.isSaving
            && !this.isResettingPairingCode
            && version !== ''
            && (!this.posAppVersionExists || version !== this.originalPosAppVersion);
    }

    getPosAppVersionButtonLabel() {
        if (this.isSavingPosAppVersion) {
            return this.posAppVersionExists ? 'Saving...' : 'Adding...';
        }
        return this.posAppVersionExists ? 'Save POS Version' : 'Add POS Version';
    }

    savePosAppVersion() {
        if (!this.canSavePosAppVersion()) {
            return;
        }

        const deviceId = this.originalIdTerminal;
        const version = this.posAppVersion.trim();
        this.isSavingPosAppVersion = true;
        this.clearModalMessage();
        this.restService.updatePosAppVersion(deviceId, version)
            .pipe(finalize(() => {
                this.isSavingPosAppVersion = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe({
                next: (response) => {
                    const parsed = this.parseResponse(response);
                    if (this.isErrorResponse(parsed) || isNullOrUndefined(parsed.PosAppVersion)) {
                        this.setModalMessage(
                            this.getResponseMessage(parsed, 'The POS app version could not be saved.'), 'error');
                        return;
                    }

                    this.posAppVersion = String(parsed.PosAppVersion.PosAppVer || '').trim();
                    this.originalPosAppVersion = this.posAppVersion;
                    this.posAppVersionExists = true;
                    this.posAppVersionLoaded = true;
                    this.setModalMessage('POS app version saved successfully.', 'success');
                },
                error: (error: HttpErrorResponse) => this.setModalMessage(
                    this.getResponseMessage(error, 'The POS app version could not be saved.'), 'error')
            });
    }

    private updatePermissions() {
        this.canUpdatePos = this.permissionService.has(AdminPermissions.UpdatePos);
        this.canResetPosPairing = this.permissionService.has(AdminPermissions.ResetPosPairing);
    }

    isModalMessageError() {
        return this.modalMessageType === 'error';
    }

    private validateTerminal() {
        if (String(this.selectedPos.IdTerminal || '').trim() === '') {
            return 'Terminal ID is required.';
        }
        const latitude = Number(this.selectedPos.Latitude);
        if (isNaN(latitude) || latitude < -90 || latitude > 90) {
            return 'Latitude must be between -90 and 90.';
        }
        const longitude = Number(this.selectedPos.Longitude);
        if (isNaN(longitude) || longitude < -180 || longitude > 180) {
            return 'Longitude must be between -180 and 180.';
        }
        const maxCarsCount = Number(this.selectedPos.MaxCarsCount);
        if (!Number.isInteger(maxCarsCount) || maxCarsCount < 0) {
            return 'Maximum cars count must be a positive whole number or zero.';
        }
        return '';
    }

    private initializeMapCoordinates() {
        const hasLatitude = !isNullOrUndefined(this.selectedPos.Latitude)
            && String(this.selectedPos.Latitude).trim() !== '';
        const hasLongitude = !isNullOrUndefined(this.selectedPos.Longitude)
            && String(this.selectedPos.Longitude).trim() !== '';
        const latitude = Number(this.selectedPos.Latitude);
        const longitude = Number(this.selectedPos.Longitude);

        if (hasLatitude && hasLongitude
            && this.isValidLatitude(latitude) && this.isValidLongitude(longitude)) {
            this.setPosMapCenter(latitude, longitude);
            return;
        }

        this.selectedPos.Latitude = this.posMapLatitude;
        this.selectedPos.Longitude = this.posMapLongitude;
    }

    private isValidLatitude(value: number) {
        return !isNaN(value) && value >= -90 && value <= 90;
    }

    private isValidLongitude(value: number) {
        return !isNaN(value) && value >= -180 && value <= 180;
    }

    private setPosMapCenter(lat: number, lng: number) {
        this.posMapLatitude = lat;
        this.posMapLongitude = lng;
        this.posMapCenter = {lat, lng};
    }

    private replaceTerminal(originalId: string, updatedTerminal: PosTerminal) {
        const index = this.terminals.findIndex(item => item.IdTerminal === originalId);
        if (index >= 0) {
            this.terminals[index] = updatedTerminal;
        }
        this.terminals.sort((left, right) =>
            String(left.ClientName || left.IdTerminal).localeCompare(String(right.ClientName || right.IdTerminal)));
        this.filterTerminals();
    }

    private contains(value: unknown, search: string) {
        return !isNullOrUndefined(value) && String(value).toLowerCase().indexOf(search) >= 0;
    }

    private getTerminalSortValue(terminal: PosTerminal, column: string) {
        switch (column) {
            case 'terminalId':
                return terminal.IdTerminal;
            case 'installationDate':
                return this.getTimeValue(terminal.EntryDate);
            case 'client':
                return terminal.ClientName;
            case 'address':
                return terminal.Address;
            case 'lastOrder':
                return this.getTimeValue(terminal.LastOrderDate);
            case 'totalOrders':
                return Number(terminal.TotalOrders || 0);
            case 'pairingCode':
                return this.isPaired(terminal) ? 'Paired' : this.getPairingCode(terminal);
            default:
                return '';
        }
    }

    private getTimeValue(value: string | Date | undefined) {
        if (isNullOrUndefined(value) || value === '') {
            return undefined;
        }
        const time = new Date(value).getTime();
        return isNaN(time) ? undefined : time;
    }

    private normalizeLanguage(value: unknown) {
        const language = isNullOrUndefined(value) ? '' : String(value).trim().toUpperCase();
        return language.indexOf('EN') === 0 ? 'EN' : 'RO';
    }

    private resetPosAppVersionState() {
        this.isLoadingPosAppVersion = false;
        this.posAppVersion = '';
        this.originalPosAppVersion = '';
        this.posAppVersionExists = false;
        this.posAppVersionLoaded = false;
        this.posAppVersionError = '';
    }

    private clampCurrentPage() {
        this.currentPage = Math.max(1, Math.min(this.currentPage, this.getTotalPages()));
    }

    private parseResponse<T>(response: T | string): T | undefined {
        if (typeof response !== 'string') {
            return response;
        }
        try {
            return JSON.parse(response) as T;
        } catch {
            return undefined;
        }
    }

    private isErrorResponse(response: ApiResponse | undefined) {
        return isNullOrUndefined(response)
            || (!isNullOrUndefined(response.StatusCode) && Number(response.StatusCode) !== 0);
    }

    private getResponseMessage(response: unknown, fallback: string) {
        const payload = response instanceof HttpErrorResponse ? response.error : response;
        const parsed = this.parseErrorResponse(payload);
        if (!isNullOrUndefined(parsed) && parsed.StatusMessage) {
            return parsed.StatusMessage;
        }
        if (!isNullOrUndefined(parsed) && parsed.Message) {
            return parsed.Message;
        }
        return fallback;
    }

    private parseErrorResponse(response: unknown): ErrorPayload | undefined {
        if (typeof response === 'string') {
            try {
                return JSON.parse(response) as ErrorPayload;
            } catch {
                return undefined;
            }
        }
        return typeof response === 'object' && response !== null ? response as ErrorPayload : undefined;
    }

    private setModalMessage(message: string, type: string) {
        this.modalMessage = message;
        this.modalMessageType = type;
    }

    private clearModalMessage() {
        this.modalMessage = '';
        this.modalMessageType = '';
    }
}
